import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Plus, Minus, Store, User } from 'lucide-react';

type FaqItem = {
    q: string;
    a: string;
};

export function FaqSection() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'consumer' | 'business'>('consumer');
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const tabs = [
        { id: 'consumer', label: t('faq.tabs.consumer'), icon: User },
        { id: 'business', label: t('faq.tabs.business'), icon: Store },
    ] as const;

    // Cast the translation result to the expected type
    const items = t(`faq.categories.${activeTab}`, { returnObjects: true }) as FaqItem[];

    return (
        <section id="faq-section" className="py-24 bg-[#FDF8F3] relative overflow-hidden">
            <div className="container mx-auto px-4 max-w-4xl relative z-10">
                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 text-[#2D1810]">
                        {t('faq.title')}
                    </h2>
                    <p className="text-lg text-[#6B5344]">
                        {t('faq.subtitle')}
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex justify-center mb-12">
                    <div className="bg-[#E5DCD3]/30 p-1.5 rounded-full inline-flex relative">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setOpenIndex(null);
                                    }}
                                    className={`relative z-10 px-6 py-3 rounded-full flex items-center gap-2 text-sm md:text-base font-medium transition-colors duration-300 ${isActive ? 'text-white' : 'text-[#6B5344] hover:text-[#2D1810]'
                                        }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTab"
                                            className="absolute inset-0 bg-[#6F4E37] rounded-full shadow-lg"
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                    <span className="relative z-10 flex items-center gap-2">
                                        <Icon className="w-4 h-4" />
                                        {tab.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* FAQ Items */}
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-4"
                >
                    {Array.isArray(items) && items.map((item, index) => {
                        const isOpen = openIndex === index;
                        return (
                            <motion.div
                                key={index}
                                initial={false}
                                className={`border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen
                                    ? 'bg-[#F5EDE4] border-[#6F4E37] shadow-sm'
                                    : 'bg-white border-[#E5DCD3] hover:border-[#D4C5B5]'
                                    }`}
                            >
                                <button
                                    onClick={() => setOpenIndex(isOpen ? null : index)}
                                    className="flex items-center justify-between p-6 text-left w-full focus:outline-none group"
                                >
                                    <span className={`text-lg font-medium pr-8 transition-colors ${isOpen ? 'text-[#2D1810]' : 'text-[#6B5344] group-hover:text-[#2D1810]'
                                        }`}>
                                        {item.q}
                                    </span>
                                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-[#6F4E37] text-white' : 'bg-[#E5DCD3]/50 text-[#6B5344]'
                                        }`}>
                                        {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    </span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                                        >
                                            <div className="px-6 pb-6 pt-0 text-[#6B5344] leading-relaxed">
                                                {item.a}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </motion.div>

            </div>

            {/* Background Decorations */}
            <div className="absolute top-1/4 left-0 w-64 h-64 bg-[#6F4E37]/5 rounded-full blur-3xl pointer-events-none -translate-x-1/2" />
            <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#D4A574]/10 rounded-full blur-3xl pointer-events-none translate-x-1/3" />
        </section>
    );
}
